import { getServer } from '../exodus-eth-server/index.js'
import { fetchGasLimit } from '../gas-estimation.js'
import { ALLOWANCE_TX_TIMEOUT, APPROVAL_GAS_LIMIT, ZERO_ALLOWANCE_ASSETS } from './constants.js'

export const isZeroAllowanceAsset = (assetName) => ZERO_ALLOWANCE_ASSETS.includes(assetName)

export async function getSpendingAllowance({ asset, fromAddress, spenderAddress }) {
  const data = asset.contract.allowance.build(fromAddress, spenderAddress)

  const res = await getServer(asset)
    .ethCall({
      data: '0x' + data.toString('hex'),
      to: asset.contract.address,
    })
    .catch(() => 0)

  return asset.currency.baseUnit(res)
}

export async function isSpendingApprovalRequired({
  asset,
  fromAddress,
  spenderAddress,
  tokenAmount,
}) {
  const allowance = await getSpendingAllowance({ asset, fromAddress, spenderAddress })
  return tokenAmount.gt(allowance)
}

export const createApprove =
  ({ sendTx }) =>
  async ({
    spenderAddress,
    asset,
    feeData,
    fromAddress,
    approveAmount,
    gasLimit,
    txInput: preTxInput,
    ...rest
  }) => {
    const baseAsset = asset.baseAsset
    const txInput =
      preTxInput ?? asset.contract.approve.build(spenderAddress, approveAmount.toBaseString())

    const toAddress = asset.contract.address
    const gasLimitOptions = {
      asset: baseAsset,
      amount: baseAsset.currency.ZERO,
      toAddress,
      txInput,
      feeData,
      isContract: true,
      fromAddress,
    }

    gasLimit = gasLimit || (await fetchGasLimit(gasLimitOptions))

    const txData = await sendTx({
      asset: baseAsset.name,
      receiver: {
        address: toAddress,
        amount: baseAsset.currency.ZERO,
      },
      txInput,
      gasPrice: feeData.gasPrice,
      gasLimit,
      silent: true,
      fromAddress,
      ...rest,
    })

    if (!txData || !txData.txId) {
      throw new Error(`Failed to approve ${asset.displayTicker} - ${spenderAddress}`)
    }

    return txData
  }

const createSendApprovalAndWatchConfirmation =
  ({ sendTx, watchTxConfirmation }) =>
  async (data) => {
    // To change the approve amount you first have to reduce the addresses`
    // allowance to zero by calling `approve(_spender, 0)` if it is not
    // already 0 to mitigate the race condition described here:
    // see: https://github.com/ethereum/EIPs/issues/20#issuecomment-263524729
    // see: https://etherscan.io/address/0xdac17f958d2ee523a2206206994597c13d831ec7#code
    const approve = createApprove({ sendTx })
    const { txId } = await approve(data)
    await watchTxConfirmation(
      { asset: data.asset.baseAsset.name, walletAccount: data.walletAccount },
      txId,
      ALLOWANCE_TX_TIMEOUT
    )

    return txId
  }

export const createApproveSpendingTokens =
  ({ sendTx, watchTxConfirmation }) =>
  async (data) => {
    const txIds = []
    const sendApprovalAndWatchConfirmation = createSendApprovalAndWatchConfirmation({
      sendTx,
      watchTxConfirmation,
    })
    if (ZERO_ALLOWANCE_ASSETS.includes(data.asset.name) && !data.approveAmount.isZero) {
      const revokeTokenApprovalTxId = await sendApprovalAndWatchConfirmation({
        ...data,
        txInput: undefined,
        approveAmount: data.asset.currency.ZERO,
      })

      txIds.push(revokeTokenApprovalTxId)
      // Hardcoded min gas limit. Otherwise `fetchGasLimit` will fail since `approve(_spender, 0)` is not yet confirmed
      // eslint-disable-next-line @exodus/mutable/no-param-reassign-prop-only
      data.gasLimit = Math.max(data.gasLimit || 0, APPROVAL_GAS_LIMIT)
    }

    const tokenApprovalTxId = await sendApprovalAndWatchConfirmation({ ...data, sendTx })
    txIds.push(tokenApprovalTxId)

    return txIds
  }

export { APPROVAL_GAS_LIMIT, ZERO_ALLOWANCE_ASSETS } from './constants.js'
